// File: src/pages/StarHubPage.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { 
    getFirestore, 
    getDoc, 
    collection, 
    query, 
    where, 
    getDocs, 
    orderBy, 
    limit, 
    startAfter,
    doc, 
    onSnapshot, 
    documentId, 
    writeBatch 
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useProfile } from '../context/ProfileContext';
import PressPost from '../components/press/PressPost';
import AddToPlaylistModal from "../components/music/AddToPlaylistModal";
import AUDTippingModal from '../components/music/AUDTipModal';
import PostDetailPage from './PostDetailPage';
import toast from 'react-hot-toast';
import Spinner from '../components/Spinner';

import sparklesIcon from '../assets/sparkles.png';

const BATCH_SIZE = 8; // 🌟 Paginate 8 posts at a time to reduce Firestore reads

const StarHubPage = () => {
    const { postId } = useParams(); 
    const location = useLocation(); 
    const navigate = useNavigate(); 

    const fromTab = new URLSearchParams(location.search).get('from') || 'home';
    const { user, isDarkMode } = useProfile();
    const [artistEngagementData, setArtistEngagementData] = useState({});

    const [isHeaderVisible, setIsHeaderVisible] = useState(true);
    const lastScrollY = useRef(0);
    const scrollContainerRef = useRef(null);

    const pendingReadUpdates = useRef(new Set());
    const observer = useRef(null);

    const [playlistModalState, setPlaylistModalState] = useState({ isOpen: false, songToAdd: null });
    const [tipModalState, setTipModalState] = useState({ isOpen: false, songToTip: null, cumulativeAmount: 0 });
    
    const [activeTab, setActiveTab] = useState('home');
    const [homePosts, setHomePosts] = useState([]);
    
    // 🌟 Paginated For You Feed State
    const [forYouPosts, setForYouPosts] = useState([]);
    const [hasUnreadForYou, setHasUnreadForYou] = useState(false);
    const [hasMoreForYou, setHasMoreForYou] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const lastVisibleDocRef = useRef(null); // Ref tracking last snapshot for pagination

    const [artistProfiles, setArtistProfiles] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [singlePost, setSinglePost] = useState(null);

    const handleOpenPlaylistModal = (song) => setPlaylistModalState({ isOpen: true, songToAdd: song });
    const handleClosePlaylistModal = () => setPlaylistModalState({ isOpen: false, songToAdd: null });

    const handleTabChange = (tab) => {
        processPendingUpdates(); 
        setActiveTab(tab);
    };

    const processPendingUpdates = useCallback(() => {
        const idsToUpdate = Array.from(pendingReadUpdates.current);
        if (idsToUpdate.length === 0 || !user?.uid) return;

        const db = getFirestore();
        const batch = writeBatch(db);

        idsToUpdate.forEach(id => {
            const feedRef = doc(db, `users/${user.uid}/starHubFeed`, id);
            batch.update(feedRef, { isRead: true });
        });

        batch.commit().catch(err => console.error("Failed to mark posts as read:", err));
        pendingReadUpdates.current.clear();
    }, [user]);

    // 🌟 Helper to fetch artist profile docs for newly loaded posts
    const fetchArtistProfiles = useCallback(async (posts) => {
        if (!posts || posts.length === 0) return;
        const db = getFirestore();
        const artistIds = [...new Set(posts.map(p => p.artistId).filter(Boolean))];
        if (artistIds.length === 0) return;

        const profilesRef = collection(db, 'artist_accounts');
        const q = query(profilesRef, where(documentId(), 'in', artistIds));
        const snapshot = await getDocs(q);
        const profiles = {};
        snapshot.forEach(docSnap => {
            profiles[docSnap.id] = { id: docSnap.id, ...docSnap.data() };
        });
        setArtistProfiles(prev => ({ ...prev, ...profiles }));
    }, []);

    // File: src/pages/StarHubPage.js (Updated fetchForYouBatch excerpt)

    const fetchForYouBatch = useCallback(async (isInitial = false) => {
        if (!user?.uid || isFetchingMore || (!hasMoreForYou && !isInitial)) return;

        setIsFetchingMore(true);
        if (isInitial) setIsLoading(true);

        try {
            const db = getFirestore();
            const feedRef = collection(db, `users/${user.uid}/starHubFeed`);

            let q = query(
                feedRef, 
                orderBy('isRead', 'asc'), 
                orderBy('createdAt', 'desc'), 
                limit(BATCH_SIZE)
            );

            if (!isInitial && lastVisibleDocRef.current) {
                q = query(
                    feedRef, 
                    orderBy('isRead', 'asc'), 
                    orderBy('createdAt', 'desc'), 
                    startAfter(lastVisibleDocRef.current),
                    limit(BATCH_SIZE)
                );
            }

            const feedSnapshot = await getDocs(q);

            if (feedSnapshot.empty) {
                setHasMoreForYou(false);
                if (isInitial) setForYouPosts([]);
                setIsLoading(false);
                setIsFetchingMore(false);
                return;
            }

            lastVisibleDocRef.current = feedSnapshot.docs[feedSnapshot.docs.length - 1];
            if (feedSnapshot.docs.length < BATCH_SIZE) {
                setHasMoreForYou(false);
            }

            const postIds = feedSnapshot.docs.map(docSnap => docSnap.id);
            const postsRef = collection(db, 'artistPosts');
            const postsQuery = query(postsRef, where(documentId(), 'in', postIds));
            const postsSnapshot = await getDocs(postsQuery);

            const postsById = {};
            postsSnapshot.forEach(docSnap => { 
                postsById[docSnap.id] = { id: docSnap.id, ...docSnap.data() }; 
            });

            const hydratedBatch = feedSnapshot.docs
                .map(feedDoc => {
                    const postData = postsById[feedDoc.id];
                    if (!postData || postData.deletedAt) return null;
                    return { ...postData, ...feedDoc.data() };
                })
                .filter(Boolean);

            await fetchArtistProfiles(hydratedBatch);

            // 🌟 FETCH ENGAGEMENT INFO FOR FOR YOU RIBBONS
            const artistIds = [...new Set(hydratedBatch.map(p => p.artistId).filter(Boolean))];
            if (artistIds.length > 0) {
                const engagementRef = collection(db, `users/${user.uid}/artistsEngagedWith`);
                const engagementQuery = query(engagementRef, where(documentId(), 'in', artistIds));
                const engagementSnapshot = await getDocs(engagementQuery);
                const engagementData = {};
                engagementSnapshot.forEach(docSnap => {
                    engagementData[docSnap.id] = docSnap.data();
                });
                setArtistEngagementData(prev => ({ ...prev, ...engagementData }));
            }

            setForYouPosts(prev => isInitial ? hydratedBatch : [...prev, ...hydratedBatch]);
            setHasUnreadForYou(hydratedBatch.some(p => !p.isRead));

        } catch (error) {
            console.error("Error fetching paginated For You feed:", error);
        } finally {
            setIsLoading(false);
            setIsFetchingMore(false);
        }
    }, [user?.uid, isFetchingMore, hasMoreForYou, fetchArtistProfiles]);

    // Handle scroll behavior & infinite scroll pagination trigger
    const handleScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        if (postId) {
            setIsHeaderVisible(true);
            return;
        }

        const currentScrollY = container.scrollTop;

        if (currentScrollY <= 0) {
            setIsHeaderVisible(true);
            lastScrollY.current = 0;
            return;
        }

        if (currentScrollY > lastScrollY.current) {
            setIsHeaderVisible(false);
        } else {
            setIsHeaderVisible(true);
        }

        lastScrollY.current = currentScrollY;

        // 🌟 INFINITE SCROLL TRIGGER: Load next batch when 150px from bottom
        const scrollBottomDistance = container.scrollHeight - container.scrollTop - container.clientHeight;
        if (activeTab === 'forYou' && scrollBottomDistance < 150 && hasMoreForYou && !isFetchingMore) {
            fetchForYouBatch(false);
        }
    }, [postId, activeTab, hasMoreForYou, isFetchingMore, fetchForYouBatch]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
        };
    }, [handleScroll]);

    // IntersectionObserver for marking unread posts as read
    useEffect(() => {
        const scrollRoot = scrollContainerRef.current;

        observer.current = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const { postId: targetId, isRead } = entry.target.dataset;
                        if (isRead === 'false' && targetId) {
                            pendingReadUpdates.current.add(targetId);
                        }
                        observer.current.unobserve(entry.target);
                    }
                });
            },
            { root: scrollRoot, threshold: 0.5 }
        );

        return () => {
            if (observer.current) observer.current.disconnect();
        };
    }, []);

    const observe = useCallback((element) => {
        if (element && observer.current) {
            observer.current.observe(element);
        }
    }, []);

    const handleOpenTipModal = async (song) => {
        if (!song || !user?.uid) return;
        const db = getFirestore();
        let cumulativeAmount = 0;

        try {
            const tipDocRef = doc(db, `users/${user.uid}/tipsGiven/${song.id}`);
            const tipDocSnap = await getDoc(tipDocRef);
            if (tipDocSnap.exists()) {
                cumulativeAmount = tipDocSnap.data().tipAmountAUD || 0;
            }
        } catch (error) {
            console.error("Error fetching tip history:", error);
            toast.error("Could not load tip history.");
        }

        setTipModalState({ isOpen: true, songToTip: song, cumulativeAmount });
    };

    const handleCloseTipModal = () => setTipModalState({ isOpen: false, songToTip: null });

    useEffect(() => () => processPendingUpdates(), [processPendingUpdates]);

    // Header visibility sync when opening/closing posts
    useEffect(() => {
        setIsHeaderVisible(true);
        if (!postId) {
            setSinglePost(null);
        }
    }, [postId]);

    // Fetch single post details when postId exists
    useEffect(() => {
        if (!postId) return;

        let isMounted = true;
        const fetchSinglePost = async () => {
            setIsLoading(true);
            try {
                const db = getFirestore();
                const postRef = doc(db, 'artistPosts', postId);
                const postSnap = await getDoc(postRef);

                if (postSnap.exists() && isMounted) {
                    const postData = { id: postSnap.id, ...postSnap.data() };
                    setSinglePost(postData);
                    await fetchArtistProfiles([postData]);
                }
            } catch (err) {
                console.error("Error fetching single post:", err);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        fetchSinglePost();
        return () => { isMounted = false; };
    }, [postId, fetchArtistProfiles]);

    // Initial feed fetch trigger based on active tab
    useEffect(() => {
        if (!user?.uid || postId) return;

        if (activeTab === 'home') {
            const loadHomeFeedOptimistic = async () => {
                const db = getFirestore();
                const cacheRef = doc(db, `users/${user.uid}/feeds/forYouCache`);

                try {
                    const cacheSnap = await getDoc(cacheRef);
                    if (cacheSnap.exists() && cacheSnap.data().feed) {
                        const cachedFeed = cacheSnap.data().feed;
                        setHomePosts(cachedFeed);
                        await fetchArtistProfiles(cachedFeed);
                    }
                } catch (err) {
                    console.error("Error reading instant feed cache:", err);
                }

                try {
                    const functions = getFunctions();
                    const getHomeFeed = httpsCallable(functions, 'getHomeFeed');
                    const result = await getHomeFeed();

                    const { feedUpdated, feed } = result.data;
                    if (feedUpdated && Array.isArray(feed)) {
                        setHomePosts(feed);
                        await fetchArtistProfiles(feed);
                    }
                } catch (error) {
                    console.error("Error running background getHomeFeed sync:", error);
                }
            };

            loadHomeFeedOptimistic();
        } 
        else if (activeTab === 'forYou') {
            // Reset and load first 8-post batch
            lastVisibleDocRef.current = null;
            setHasMoreForYou(true);
            fetchForYouBatch(true);
        }
    }, [user?.uid, activeTab, postId, fetchArtistProfiles]);

    const getTabClasses = (tabName) => {
        const isActive = activeTab === tabName;
        if (isActive) return 'border-b-2 border-brand-pink text-[#ff00cc]';
        return isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-gray-400 hover:text-slate-600';
    };

    const visiblePosts = activeTab === 'home' ? homePosts : forYouPosts;

    return (
        <div className={`relative h-screen -mt-6 overflow-hidden transition-colors duration-300 ${
            isDarkMode ? 'bg-zinc-950' : 'bg-white'
        }`}>
            {/* Dynamic Header */}
            <header className={`absolute top-0 w-full z-20 transition-all duration-300 border-b ease-in-out ${
                isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
            } ${
                isDarkMode ? 'bg-zinc-900/90 border-zinc-800/80 backdrop-blur-md' : 'bg-white border-slate-100'
            }`}>
                {postId ? (
                    <div className="flex items-center h-9 px-4">
                        <button 
                            type="button"
                            onClick={() => navigate(`/feed?from=${activeTab}`)} 
                            className={`p-2 -ml-2 flex items-center justify-center h-full rounded-full transition-colors ${
                                isDarkMode ? 'hover:bg-zinc-800' : 'hover:bg-slate-50'
                            }`}
                            title="Back to Feed"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${isDarkMode ? 'text-zinc-300' : 'text-gray-700'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <span className={`ml-1 text-xs font-black uppercase tracking-wider ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            Post
                        </span>
                    </div>
                ) : (
                    <div className="flex justify-center items-center flex-shrink-0 h-9">
                        <button 
                            type="button"
                            onClick={() => handleTabChange('home')}
                            className={`flex items-center gap-x-2 px-6 font-black text-xs uppercase tracking-widest transition-all h-full ${getTabClasses('home')}`}
                        >
                            <span>Home</span>
                        </button>
                        <button 
                            type="button"
                            onClick={() => handleTabChange('forYou')}
                            className={`relative flex items-center justify-center px-6 font-black text-xs uppercase tracking-widest transition-all h-full ${getTabClasses('forYou')}`}
                        >
                            <span>For You</span>
                            {hasUnreadForYou && (
                                <img 
                                    src={sparklesIcon} 
                                    alt="Unread posts" 
                                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-7 pointer-events-none mix-blend-screen" 
                                />
                            )}
                        </button>
                    </div>
                )}
            </header>

            {/* Persistent Main Feed */}
            <main 
                ref={scrollContainerRef} 
                className={`h-full overflow-y-scroll p-4 pt-12 pb-40 custom-scrollbar ${postId ? 'hidden' : 'block'}`}
            >
                {isLoading && visiblePosts.length === 0 ? (
                    <div className="h-48 flex items-center justify-center">
                        <Spinner />
                    </div>
                ) : visiblePosts.length > 0 ? (
                    <div className="flex flex-col items-center space-y-4 max-w-2xl mx-auto">
                        {visiblePosts.map(post => (
                            <PressPost 
                                key={post.id} 
                                post={post}
                                observe={observe} 
                                artistProfile={artistProfiles[post.artistId]}
                                engagementInfo={activeTab === 'forYou' ? artistEngagementData[post.artistId] : null}
                                sourceContext={activeTab} 
                                onAddToPlaylistClick={handleOpenPlaylistModal}
                                onTipClick={handleOpenTipModal}
                            />
                        ))}

                        {/* Paginated Batch Loader Spinner */}
                        {isFetchingMore && (
                            <div className="py-4 flex justify-center">
                                <Spinner />
                            </div>
                        )}
                    </div>
                ) : (
                    <p className={`text-center text-xs font-bold uppercase tracking-widest mt-12 max-w-sm mx-auto leading-relaxed ${
                        isDarkMode ? 'text-zinc-600' : 'text-gray-400'
                    }`}>
                        {activeTab === 'home' 
                            ? "No new posts right now. Check back later!" 
                            : "Subscribe to artists to see their posts here!"
                        }
                    </p>
                )}
            </main>

            {/* Post Detail View Container */}
            {postId && (
                <div className="h-full overflow-y-scroll p-4 pt-12 pb-40 custom-scrollbar">
                    {isLoading && !singlePost ? (
                        <div className="h-48 flex items-center justify-center">
                            <Spinner />
                        </div>
                    ) : (
                        <PostDetailPage 
                            post={singlePost} 
                            artistProfile={singlePost ? artistProfiles[singlePost.artistId] : null} 
                        />
                    )}
                </div>
            )}

            {/* Modals */}
            <AddToPlaylistModal 
                isOpen={playlistModalState.isOpen}
                onClose={handleClosePlaylistModal}
                songToAdd={playlistModalState.songToAdd}
            />
            <AUDTippingModal 
                isOpen={tipModalState.isOpen}
                onClose={handleCloseTipModal}
                songToTip={tipModalState.songToTip}
                cumulativeSongTip={tipModalState.cumulativeAmount}
            />
        </div>
    );
};

export default StarHubPage;